// Crypto & Session Token Helpers for Synced Audiobooks Protection Architecture
// Web Crypto API compatible (Node.js 18+, Cloudflare Workers, and Browser)

const SESSION_SECRET = "libritui-audiobook-sync-protection-key-2026";

async function getHmacKey(secret: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  return await crypto.subtle.importKey(
    "raw",
    enc.encode(secret),
    { name: "HMAC", hash: { name: "SHA-256" } },
    false,
    ["sign", "verify"]
  );
}

async function getAesKey(seed: string): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.digest("SHA-256", enc.encode(seed));
  return await crypto.subtle.importKey(
    "raw",
    keyMaterial,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"]
  );
}

export interface SessionPayload {
  userId: string;
  bookId: string;
  issuedAt: number;
  exp: number; // TTL 120s
  lastTime?: number;
}

export async function createSessionToken(userId: string, bookId: string): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  const payload: SessionPayload = {
    userId,
    bookId,
    issuedAt: now,
    exp: now + 120, // 2 minutes TTL
    lastTime: 0
  };

  const jsonStr = JSON.stringify(payload);
  const enc = new TextEncoder();
  const key = await getHmacKey(SESSION_SECRET);
  const sig = await crypto.subtle.sign("HMAC", key, enc.encode(jsonStr));

  const base64Payload = btoa(jsonStr).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  const base64Sig = btoa(String.fromCharCode(...new Uint8Array(sig))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

  return `${base64Payload}.${base64Sig}`;
}

export async function verifySessionToken(token: string): Promise<SessionPayload | null> {
  if (!token || !token.includes('.')) return null;

  try {
    const [b64Payload, b64Sig] = token.split('.');
    const jsonStr = atob(b64Payload.replace(/-/g, '+').replace(/_/g, '/'));
    const payload: SessionPayload = JSON.parse(jsonStr);

    const now = Math.floor(Date.now() / 1000);
    if (now > payload.exp) {
      return null; // Expired token
    }

    const key = await getHmacKey(SESSION_SECRET);
    const enc = new TextEncoder();

    const sigStr = atob(b64Sig.replace(/-/g, '+').replace(/_/g, '/'));
    const sigBytes = new Uint8Array(sigStr.length);
    for (let i = 0; i < sigStr.length; i++) {
      sigBytes[i] = sigStr.charCodeAt(i);
    }

    const isValid = await crypto.subtle.verify("HMAC", key, sigBytes, enc.encode(jsonStr));
    if (!isValid) return null;

    return payload;
  } catch (err) {
    return null;
  }
}

export async function encryptWindowPayload(data: any, token: string): Promise<{ ciphertext: string; iv: string }> {
  const aesKey = await getAesKey(token);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const jsonStr = JSON.stringify(data);

  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    aesKey,
    enc.encode(jsonStr)
  );

  const b64Ciphertext = btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  const b64Iv = btoa(String.fromCharCode(...iv));

  return { ciphertext: b64Ciphertext, iv: b64Iv };
}

export async function decryptWindowPayload(ciphertext: string, ivB64: string, token: string): Promise<any> {
  const aesKey = await getAesKey(token);

  const cipherStr = atob(ciphertext);
  const cipherBytes = new Uint8Array(cipherStr.length);
  for (let i = 0; i < cipherStr.length; i++) {
    cipherBytes[i] = cipherStr.charCodeAt(i);
  }

  const ivStr = atob(ivB64);
  const ivBytes = new Uint8Array(ivStr.length);
  for (let i = 0; i < ivStr.length; i++) {
    ivBytes[i] = ivStr.charCodeAt(i);
  }

  const decrypted = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ivBytes },
    aesKey,
    cipherBytes
  );

  const dec = new TextDecoder();
  return JSON.parse(dec.decode(decrypted));
}

// Injects random phonetic ghost spans into HTML/Text to pollute scraper .innerText / DOM extraction
export function injectGhostNodes(htmlOrText: string): string {
  if (!htmlOrText) return htmlOrText;
  const ghostWords = ['zephy', 'kalon', 'brim', 'lumix', 'vort', 'pyro', 'quar', 'flit', 'zorn', 'vexo'];
  
  const ghost = ghostWords[Math.floor(Math.random() * ghostWords.length)];
  const ghostSpan = `<span aria-hidden="true" style="display:none!important;opacity:0!important;position:absolute!important;pointer-events:none!important;"> ${ghost} </span>`;

  if (htmlOrText.includes('</p>')) {
    return htmlOrText.replace(/<\/p>/g, `${ghostSpan}</p>`);
  }
  
  const words = htmlOrText.split(' ');
  if (words.length <= 4) return htmlOrText;

  const insertIdx = Math.floor(words.length / 2);
  words.splice(insertIdx, 0, ghostSpan);
  return words.join(' ');
}
